<script>
    export let data = {};

    // components
    import Button from "./../Button.svelte";
    import CheckList from "./../CheckList.svelte";
    import DataGrid from "../composites/DataGrid.svelte";
    import DisclosureCard from "../DisclosureCard.svelte";
    import HorizontalRule from "./../presentational/HorizontalRule.svelte";
    import PurchaseProduct from "../composites/PurchaseProduct.svelte";
    import View from "../presentational/View.svelte";
    import VPNDevice from "./VPNDevice.svelte";
    import VPNLogo from "./../presentational/logos/VPNLogo.svelte";
    import PeaceSign from "./../presentational/icons/PeaceSign.svelte";

    // functions
    import { AppData } from "../../stores.js";
    import { createEventDispatcher, onMount, beforeUpdate } from "svelte";
    import { emailVPNDownloadLinks } from "../../api/client.js";
    import { updateDevice, deleteDevice } from "../../api/client.js";
    import { processSubscription } from "../../utilities/data-wrangling.js";
    import { t } from "../../i18n";

    import { logVPNExitSurvey } from "../../api/log.js";
    import CustomRadioExitSurvey from "../CustomRadioExitSurvey.svelte";

    // TODO filter for the active subscription, exclude inactives, etc.
    const isFreeTrial = data.product.plan_id === "vpn_free_trial";
    // Sort subscriptions descending in order of created time
    // so that the most recently-created subscription comes first.
    // This doesn't handle the case where the user accidentally has
    // more than 1 subscription but it's a starting point where we
    // could display multiple active subscriptions and reconcile
    // them, if required.
    if (data.subscription) {
        data.subscription.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
    }

    let subscription = {};
    let nextBillString = "";
    let nextBillDate = "";
    let isTimeBound = false; // Assume it's a subscription

    // Process the subscription, set formatted vars
    const setVars = processedSub => {
        subscription = processedSub["subscription"];
        nextBillString = processedSub["nextBillString"];
        nextBillDate = processedSub["nextBillDate"];
        isTimeBound = processedSub["isTimeBound"];
    };
    onMount(() => {
        if (data.subscription) {
            data.subscription.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
        }
        setVars(processSubscription(data.subscription, isFreeTrial, $t));
    });
    beforeUpdate(() => {
        // Update the data.
        if (data.subscription) {
            // TODO this is a little hacky. Right now we only visualize 1 subscription...
            // if multiple come in we only show the latest since we assume it's the most
            // recent one the user cares about. In the future we will paginate and have
            // a multiple-subscription reconciliation flow.
            data.subscription.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
        }
        setVars(processSubscription(data.subscription, isFreeTrial, $t));
    });
    const dispatch = createEventDispatcher();
    const vpnChecklist = {
        title: "Get Sweet VPN Stuff",
        link: {
            href: "about:blank",
            text: "Learn More",
        },
        checkItems: [
            "Impress your friends",
            "Watch US Netflix anywhere",
            "Have super secret spy-level location blocking",
            "Confuse those sneaky hackers",
            "Get peoples' approval",
        ],
    };
    const productDetails = {
        name: "AdBlock VPN",
        prices: [
            {
                amountString: "4.00",
                recurring: true,
                recurringFrequency: "monthly",
            },
            {
                amountString: "35.00",
                recurring: true,
                recurringFrequency: "yearly",
            },
        ],
        currencies: [
            {
                symbol: "$",
                text: "USD",
            },
            {
                symbol: "£",
                text: "GBP",
            },
            {
                symbol: "€",
                text: "EUR",
            },
        ],
        priceSummaryString: "$4.00 a month or $35 yearly",
    };

    const iapStoreToCancelSubInstructions = {
        "apple_app_store": $t('apple_app_store_submgmt_instructions'),
        "google_play_store": $t('google_play_store_submgmt_instructions'),
    };

    const getPlatformSupported = () => true; // TODO: detect plaform :)
    $: isPlatformSupported = getPlatformSupported();
    $: isActive = data.product ? data.product.status === "active" : false;
    $: isCancelled = data.subscription[0].subscription_data.cancelled === true;
    $: isInAppPurchase = data.subscription[0].subscription_data.iap_store !== null;
    $: inAppPurchaseInstructions = isInAppPurchase ? iapStoreToCancelSubInstructions[data.subscription[0].subscription_data.iap_store] : "";
    $: devices = data.product && data.product.options && data.product.options.devices ? Object.keys(data.product.options.devices)
        .map((key) => data.product.options.devices[key])
        .sort((a, b) => new Date(b.last) - new Date(a.last)) : null;

    const state = {
        isCancelling: false,
        isPurchasing: false,
        feedbackFormHasSelected: false,
        needsExitSurvey: false,
    };
    $: isCancelling = state.isCancelling === true;
    $: isPurchasing = state.isPurchasing === true;
    $: canSubmitFeedbackForm = state.feedbackFormHasSelected === true;
    $: needsExitSurvey = state.needsExitSurvey === true || (localStorage && localStorage.getItem("vpnbeta-needs-exit-survey") === "1");

    const handleUpdateDevice = ({ device_id, name }) => {
        updateDevice({
            device_id,
            dataToUpdate: { name },
            onSuccess: function () {
                // TODO maybe make thie cleaner
                dispatch("message", {
                    action: "STATE_UPDATED",
                    data: {},
                });
            },
            onError: function (err) {
                console.log("update device error", err);
            },
        });
    };

    const handleDeleteDevice = ({ device_id }) => {
        // devices = devices.filter((d) => d.device_id !== device_id);
        deleteDevice({
            device_id,
            onSuccess: function(result) {
                console.log("delete device on success", device_id);
                console.log("Devices before", devices);
                devices = devices.filter((d) => d.device_id !== device_id);
                console.log("Devices after", devices);
            },
            onError: (err) => console.log("delete device error", err),
        });
    };

    const handleEmailDownloadLinks = () => {
        emailVPNDownloadLinks({
            onSuccess: function (data) {
                console.log("emailed the links", data);
            },
            onError: function (err) {
                console.log("didnt email those links oops", err);
            },
        });
    };

    const handleMessage = (msg) => {
        switch (msg.detail.action) {
            case "UPDATE":
                handleUpdateDevice(msg.detail.data);
                break;
            case "DELETE":
                handleDeleteDevice(msg.detail.data);
                break;
            default:
                console.log("default handleMessage");
                break;
        }
    };

    const onChange = (e) => {
        state.feedbackFormHasSelected = true;
        const formData = new FormData(e.currentTarget);
        const data = {};
        for (let field of formData) {
            const [key, value] = field;
            data[key] = value;
        }
        state.selectedChoice = data.cancel_reason;
    };

    const selectRadioOption = (optionName) => {
        const maybeElement = document.querySelector(`input[value=${optionName}]`);
        if (maybeElement) {
            maybeElement.click();
        }
    }

    const onSubmit = (e) => {
        if (!state.feedbackFormHasSelected) {
            return;
        }
        const formData = new FormData(e.target);
        const data = {};
        for (let field of formData) {
            const [key, value] = field;
            data[key] = value;
        }
        let accountUUID = "";
        try {
            accountUUID = localStorage.getItem("adblock-app-account-id-key");
        } catch (err) {
            console.error("couldnt fetch account uuid for feedback form:", err);
        }
        const explain = ["t1", "t2", "t3", "t4","t5"].filter(k => data[k]).map(key => `${key}:${data[key].trim()}`).join(" ").trim();
        const toSubmit = {
            account_uuid: accountUUID,
            cancel_reason: data.cancel_reason,
            explain,
        };
        logVPNExitSurvey(toSubmit);
        localStorage.removeItem("vpnbeta-needs-exit-survey");
        state.needsExitSurvey = false;
    };
</script>

{#if isFreeTrial}
    <!-- TODO maybe break out "Alpha-to-Beta Free Trial VPNBeta view"
         into another component so it's less messy later -->
{#if isActive}
    <DisclosureCard logoComponent="{VPNLogo}" leadingComponentActive="{PeaceSign}" isActive="{true}" collapsedDescription="{nextBillString}" expandedDescription="{nextBillString}" isCollapsed="{false}" on:message>
        <div class="row sub-data">
            {#if isTimeBound}
                <View viewType="{'card-wrap'}">
                    <div class="row justify-content-space-between">
                        <h3>{productDetails.name}</h3>
                    </div>
                    <HorizontalRule />
                    <DataGrid data="{subscription}" />
                </View>
            {:else}
                <View viewType="{'card-wrap'}">
                    <div class="row justify-content-space-between">
                        <h3>{$t('subscription_for_product', {"$replace$": productDetails.name})}</h3>
                    </div>
                    <HorizontalRule />
                    <DataGrid data="{subscription}" />
                </View>
            {/if}
        </div>

        <div class="row">
            <View viewType="{'card-wrap'}">
                <h3>{$t('active_devices')}</h3>
                {#if devices.length > 0}
                    <HorizontalRule />
                {/if}
                <div class="row collapse-to-col">
                    {#each devices as d, idx (d.device_id)}
                        <!--
                        (d.device_id) is a key so we can uniquely identify each item in the each block.
                        https://svelte.dev/tutorial/keyed-each-blocks -->
                        <!-- d like: { created: "2020-01-01T12:12:12.000Z", device_id: "did1",
                            last: "2020-01-01T12:12:12.000Z", modified: "2020-01-01T12:12:12.000Z", name: "Iain's MacBook"}
                        -->
                        {#if idx !== devices.length - 1}
                            <VPNDevice data="{d}" on:message="{handleMessage}" classNames="{'underline'}" />
                        {:else}
                            <VPNDevice data="{d}" on:message="{handleMessage}" classNames="{'underline-dark'}" />
                        {/if}
                    {/each}
                </div>
                <div class="row justify-content-flex-end">
                    {$t('number_devices_connected', {"$currentDeviceCount$": devices.length, "$maximumDeviceCount$": 6})}
                </div>
            </View>
        </div>
        <HorizontalRule />
        <div class="row align-items-center collapse-to-col justify-content-space-between">
            <div class="row align-items-center">
                <i class="material-icons-outlined">mail</i>
                <a href="/" on:click|preventDefault="{handleEmailDownloadLinks}">
                    {$t('send_download_links', {"$replace$": ($AppData.profile.emails.filter((email) => email.primary)[0] || {}).address})}
                </a>
                <!-- {#if isPlatformSupported}
                    <Button
                        buttonText="{'Download'}"
                        classNames="{'far-right-auto short w176px'}"
                        on:click="{() => console.log('DOWNLOAD')}"
                    />
                {/if} -->
            </div>
        </div>
    </DisclosureCard>
{:else if isPurchasing}
    <DisclosureCard logoComponent="{VPNLogo}" isActive="{false}" isCollapsed="{false}" on:message>
        <PurchaseProduct productDetails="productDetails" />
    </DisclosureCard>
{:else}
    <DisclosureCard logoComponent="{VPNLogo}" isActive="{false}" on:message>
        <CheckList {...vpnChecklist} />
        <HorizontalRule />
        <div class="row align-items-center collapse-to-col">
            <i class="material-icons-outlined">credit_card</i>
            {productDetails.priceSummaryString}
            <Button
                buttonText="Get {productDetails.name}"
                classNames="{'far-right-auto'}"
                on:click="{() => (state.isPurchasing = true)}"
            />
        </div>
    </DisclosureCard>
{/if}

{:else}

{#if isActive}
    <!-- TODO break out into another component so it's less messy later -->
    <DisclosureCard logoComponent="{VPNLogo}" leadingComponentActive="{PeaceSign}" isActive="{true}" collapsedDescription="{nextBillString}" expandedDescription="{nextBillString}" isCollapsed="{false}" on:message>
        <div class="row sub-data">
            <View viewType="{'card-wrap'}">
                {#if isCancelling}
                    <div class="row justify-content-space-between">
                        <h3>{$t('cancel_subscription')}</h3>
                    </div>
                    <HorizontalRule />
                    <div class="row" style="margin-bottom:24px;">
                        {$t('cancel_are_you_sure')}
                        {$t('you_will_have_access_until', {"$productName$": productDetails.name, "$nextBillDate$": nextBillDate})}.
                    </div>
                    <div class="row collapse-to-col justify-content-flex-end">
                        <Button
                            classNames="{'outline tertiary'}"
                            buttonText="{$t('no_keep_product', {"$replace$": productDetails.name})}"
                            on:click="{() => (state.isCancelling = false)}"
                        />
                        <Button
                            classNames="{'tertiary'}"
                            buttonText="{$t('yes_cancel')}"
                            on:click="{() => {
                                dispatch('message', { action: 'CANCEL_SUBSCRIPTION', data });
                                state.isCancelling = false;
                                localStorage.setItem("vpnbeta-needs-exit-survey", "1");
                                state.needsExitSurvey = true;
                            }}"
                        />
                    </div>
                    <HorizontalRule />
                {:else if isCancelled}
                    <div class="row justify-content-space-between">
                        <h3>{$t('subscription_for_product', {"$replace$": productDetails.name})}</h3>
                    </div>
                    <HorizontalRule />
                    <DataGrid data="{subscription}" />
                    {#if needsExitSurvey}
                        <HorizontalRule />
                        <div class="row justify-content-flex-start align-items-center" style="margin:8px auto;">
                            <div style="font-weight:700;" class="row align-items-center">
                                <i class="material-icons checked" style="color:#10B501; margin-right: 20px;">check_circle</i>
                                {$t('you_have_canceled')}
                                {$t('help_improve_vpn_tell_us_why_cancel')}
                            </div>
                        </div>
                        <form on:submit|preventDefault={onSubmit} on:change={onChange}>
                            <div class="row justify-content-flex-start align-items-center radio-choice">
                                <CustomRadioExitSurvey name="cancel_reason" value="missing_features" checked={state.selectedChoice === "missing_features"}></CustomRadioExitSurvey>
                                <label for="missing_features" on:click={() => selectRadioOption("missing_features")}>{$t('vpn_feedback_missing_features')}</label>
                                {#if state.selectedChoice == "missing_features"}
                                    <textarea name="t1" rows="4" cols="70" style="color:#333;border-radius:6px;padding:24px 48px;font-size:18px;font-family:Lato;max-width:80%;" maxlength="256" placeholder="Can you tell us a little more about that?" i18n_placeholder="uninstall_enter_reason"></textarea>
                                {/if}
                            </div>
                            <div class="row justify-content-flex-start align-items-center radio-choice">
                                <CustomRadioExitSurvey name="cancel_reason" value="not_in_my_country" checked={state.selectedChoice === "not_in_my_country"}></CustomRadioExitSurvey>
                                <label for="not_in_my_country" on:click={() => selectRadioOption("not_in_my_country")}>{$t('vpn_feedback_no_works_in_country')}</label>
                                {#if state.selectedChoice == "not_in_my_country"}
                                    <textarea name="t2" rows="4" cols="70" style="color:#333;border-radius:6px;padding:24px 48px;font-size:18px;font-family:Lato;max-width:80%;" maxlength="256" placeholder="Can you tell us a little more about that?" i18n_placeholder="uninstall_enter_reason"></textarea>
                                {/if}
                            </div>
                            <div class="row justify-content-flex-start align-items-center radio-choice">
                                <CustomRadioExitSurvey name="cancel_reason" value="cant_access_content" checked={state.selectedChoice === "cant_access_content"}></CustomRadioExitSurvey>
                                <label for="cant_access_content" on:click={() => selectRadioOption("cant_access_content")}>{$t('vpn_feedback_no_access_content')}</label>
                                {#if state.selectedChoice == "cant_access_content"}
                                    <textarea name="t3" rows="4" cols="70" style="color:#333;border-radius:6px;padding:24px 48px;font-size:18px;font-family:Lato;max-width:80%;" maxlength="256" placeholder="Can you tell us a little more about that?" i18n_placeholder="uninstall_enter_reason"></textarea>
                                {/if}
                            </div>
                            <div class="row justify-content-flex-start align-items-center radio-choice">
                                <CustomRadioExitSurvey name="cancel_reason" value="missing_location" checked={state.selectedChoice === "missing_location"}></CustomRadioExitSurvey>
                                <label for="missing_location" on:click={() => selectRadioOption("missing_location")}>{$t('vpn_feedback_missing_location')}</label>
                                {#if state.selectedChoice == "missing_location"}
                                    <textarea name="t4" rows="4" cols="70" style="color:#333;border-radius:6px;padding:24px 48px;font-size:18px;font-family:Lato;max-width:80%;" maxlength="256" placeholder="Can you tell us a little more about that?" i18n_placeholder="uninstall_enter_reason"></textarea>
                                {/if}
                            </div>
                            <div class="row justify-content-flex-start align-items-center radio-choice">
                                <CustomRadioExitSurvey name="cancel_reason" value="other_reason" checked={state.selectedChoice === "other_reason"}></CustomRadioExitSurvey>
                                <label for="other_reason" style="width:50%;" on:click={() => selectRadioOption("other_reason")}>{$t('uninstall_reason_other')}</label>
                                {#if state.selectedChoice == "other_reason"}
                                    <textarea name="t5" rows="4" cols="70" style="color:#333;border-radius:6px;padding:24px 48px;font-size:18px;font-family:Lato;max-width:80%;" maxlength="256" placeholder="Can you tell us a little more about that?" i18n_placeholder="uninstall_enter_reason"></textarea>
                                {/if}
                            </div>
                            {#if canSubmitFeedbackForm}
                                <Button
                                    buttonText="{$t('submit_feedback')}"
                                    classNames="{'far-right-auto feedback-submit'}"
                                />
                            {:else}
                                <Button
                                    buttonText="{$t('submit_feedback')}"
                                    classNames="{'far-right-auto feedback-submit inactive'}"
                                />
                            {/if}
                    </form>
                    {/if}
                {:else if isTimeBound}
                    <div class="row justify-content-space-between">
                        <h3>{productDetails.name}</h3>
                    </div>
                    <HorizontalRule />
                    <DataGrid data="{subscription}" />
                {:else}
                    <div class="row justify-content-space-between">
                            <h3>{$t('subscription_for_product', {"$replace$": productDetails.name})}</h3>
                        {#if isInAppPurchase}
                        <div class="row justify-content-flex-end" style="width:auto;">{inAppPurchaseInstructions}</div>
                    {:else}
                        <a href="/" on:click|preventDefault="{() => (state.isCancelling = true)}">
                            {$t('cancel_subscription')}
                        </a>
                    {/if}
                    </div>
                    <HorizontalRule />
                    <DataGrid data="{subscription}" />
                {/if}
            </View>
        </div>

        <div class="row">
            <View viewType="{'card-wrap'}">
                <h3>{$t('active_devices')}</h3>
                {#if devices.length > 0}
                    <HorizontalRule />
                {/if}
                <div class="row collapse-to-col">
                    {#each devices as d, idx (d.device_id)}
                        <!-- 
                        (d.device_id) is a key so we can uniquely identify each item in the each block.
                        https://svelte.dev/tutorial/keyed-each-blocks -->
                        <!-- d like: { created: "2020-01-01T12:12:12.000Z", device_id: "did1",
                            last: "2020-01-01T12:12:12.000Z", modified: "2020-01-01T12:12:12.000Z", name: "Iain's MacBook"}
                        -->
                        {#if idx !== devices.length - 1}
                            <VPNDevice data="{d}" on:message="{handleMessage}" classNames="{'underline'}" />
                        {:else}
                            <VPNDevice data="{d}" on:message="{handleMessage}" classNames="{'underline-dark'}" />
                        {/if}
                    {/each}
                </div>
                <div class="row justify-content-flex-end">
                    {$t('number_devices_connected', {"$currentDeviceCount$": devices.length, "$maximumDeviceCount$": 6})}
                </div>
            </View>
        </div>
        <HorizontalRule />
        <div class="row align-items-center collapse-to-col justify-content-space-between">
            <div class="row align-items-center">
                <i class="material-icons-outlined">mail</i>
                <a href="/" on:click|preventDefault="{handleEmailDownloadLinks}">
                    {$t('send_download_links', {"$replace$": ($AppData.profile.emails.filter((email) => email.primary)[0] || {}).address})}
                </a>
                <!-- {#if isPlatformSupported}
                    <Button
                        buttonText="{'Download'}"
                        classNames="{'far-right-auto short w176px'}"
                        on:click="{() => console.log('DOWNLOAD')}"
                    />
                {/if} -->
            </div>
        </div>
    </DisclosureCard>
{:else if isPurchasing}
    <DisclosureCard logoComponent="{VPNLogo}" isActive="{false}" isCollapsed="{false}" on:message>
        <PurchaseProduct productDetails="productDetails" />
    </DisclosureCard>
{:else}
    <DisclosureCard logoComponent="{VPNLogo}" isActive="{false}" on:message>
        <CheckList {...vpnChecklist} />
        <HorizontalRule />
        <div class="row align-items-center collapse-to-col">
            <i class="material-icons-outlined">credit_card</i>
            {productDetails.priceSummaryString}
            <Button
                buttonText="Get {productDetails.name}"
                classNames="{'far-right-auto'}"
                on:click="{() => (state.isPurchasing = true)}"
            />
        </div>
    </DisclosureCard>
{/if}
{/if}

<style>
    div.sub-data {
        width: 100%;
        margin-bottom: 16px;
    }
    label {
        cursor: pointer;
    }
    div.radio-choice {
        margin: 16px 0px;
    }
    textarea {
        margin-top: 8px;
        display: flex;
    }
</style>
